<template>
  <div>
    <template v-if="state === 'loading'">
      <ModalSpinner></ModalSpinner>
    </template>
    <template v-else>
      <ModalHeaderContent>
        <template v-if="isCloseVisible" #left>
          <Close @click="close"></Close>
        </template>
        <template #default>
          <div v-if="state === 'complete'" class="user-select-none" v-html="html"></div>
          <div v-else class="text-center" v-html="t('error')"></div>
        </template>
      </ModalHeaderContent>
    </template>
  </div>
</template>

<script lang="ts">
// these params are necessary for modal service
export default { isPaymentModal: false, isForceAllowedModal: true };
</script>

<script lang="ts" setup>
import { replaceMmaLegalVariables } from '@project-gd-x/shared-web/src/helpers/legal-formatter/legal-formatter';
import { PropType, ref, watch } from 'vue';

import Close from '@/components/layout/components/header/components/close/Close.vue';
import ModalHeaderContent from '@/components/modal/ModalHeaderContent.vue';
import ModalSpinner from '@/components/modal/ModalSpinner.vue';
import { getUserId } from '@/helpers/auth/auth';
import { useCountry } from '@/hooks/use-country/use-country';
import { goBackOrGo } from '@/router';
import { GetTextPageArgumentPageId } from '@/services/api/page/page-api';
import { getLocale, Locale, useI18n } from '@/services/i18n';

import { messages } from './i18n';

type State = 'loading' | 'complete' | 'error';

const props = defineProps({
  pageId: {
    type: String as PropType<GetTextPageArgumentPageId>,
    required: true,
  },
  isCloseVisible: {
    type: Boolean,
    default: true,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n({ messages });

const state = ref<State>('loading');
const html = ref<string>('');

watch(
  () => props.pageId,
  () => {
    load();
  },
);

function close() {
  if (props.isModal) {
    emit('close');
  } else {
    goBackOrGo(getUserId() ? 'search' : 'homepage');
  }
}

function importPageHtml(pageId: GetTextPageArgumentPageId) {
  const locale = getLocale();

  const pageImportedList = {
    en: {
      terms: () => import('./content/en/terms-en.html?raw'),
      privacy: () => import('./content/en/privacy-en.html?raw'),
      cookie: () => import('./content/en/cookie-en.html?raw'),
      'anti-scam': () => import('./content/en/anti-scam-en.html?raw'),
      membership: () => import('./content/en/membership-en.html?raw'),
      'consent-policy': () => import('./content/en/consent-policy-en.html?raw'),
      'no-sexual-content-policy': () => import('./content/en/no-sexual-content-policy-en.html?raw'),
    },

    es: {
      terms: () => import('./content/es/terms-es.html?raw'),
      privacy: () => import('./content/es/privacy-es.html?raw'),
      cookie: () => import('./content/es/cookie-es.html?raw'),
      'anti-scam': () => import('./content/es/anti-scam-es.html?raw'),
      membership: () => import('./content/es/membership-es.html?raw'),
      'consent-policy': () => import('./content/es/consent-policy-es.html?raw'),
      'no-sexual-content-policy': () => import('./content/es/no-sexual-content-policy-es.html?raw'),
    },

    de: {
      terms: () => import('./content/de/terms-de.html?raw'),
      privacy: () => import('./content/de/privacy-de.html?raw'),
      cookie: () => import('./content/de/cookie-de.html?raw'),
      'anti-scam': () => import('./content/de/anti-scam-de.html?raw'),
      membership: () => import('./content/de/membership-de.html?raw'),
      'consent-policy': () => import('./content/de/consent-policy-de.html?raw'),
      'no-sexual-content-policy': () => import('./content/de/no-sexual-content-policy-de.html?raw'),
    },
  };

  switch (locale) {
    case Locale.Es:
      return pageImportedList.es[pageId]();
    case Locale.De:
      return pageImportedList.de[pageId]();
    default:
      return pageImportedList.en[pageId]();
  }
}

async function load() {
  try {
    state.value = 'loading';

    let { default: contentHtml } = await importPageHtml(props.pageId);

    const country = useCountry();
    await country.load();

    if (country.country.value) {
      html.value = replaceMmaLegalVariables(contentHtml, {
        countryAlpha3: country.country.value?.country.isoCodeAlpha3,
        baseUrl: props.isCloseVisible ? '/text-page/' : '/text-page-raw/',
      });

      state.value = 'complete';
    }
  } catch (e) {
    console.error(e);
    state.value = 'error';
  }
}

load();
</script>
